import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface HistoryEntry {
  type: 'client' | 'order' | 'orderLine' | 'equipment';
  label: string;
  term: string;
  searchType?: string;
  timestamp: Date;
  icon: string;
}

const STORAGE_KEY = 'vizor-history';
const MAX_ENTRIES = 10;

@Injectable({ providedIn: 'root' })
export class HistoryService {
  private readonly _history$ = new BehaviorSubject<HistoryEntry[]>(this._load());

  readonly history$ = this._history$.asObservable();

  add(entry: Omit<HistoryEntry, 'timestamp'>): void {
    const entries = this._history$.value;
    const newEntry: HistoryEntry = { ...entry, timestamp: new Date() };

    // Remove duplicate if same type + term + searchType
    const filtered = entries.filter(
      (e) => !(e.type === newEntry.type && e.term === newEntry.term && e.searchType === newEntry.searchType),
    );

    const updated = [newEntry, ...filtered].slice(0, MAX_ENTRIES);
    this._history$.next(updated);
    this._save(updated);
  }

  clear(): void {
    this._history$.next([]);
    localStorage.removeItem(STORAGE_KEY);
  }

  private _load(): HistoryEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as HistoryEntry[];
      return parsed.map((e) => ({ ...e, timestamp: new Date(e.timestamp) }));
    } catch {
      return [];
    }
  }

  private _save(entries: HistoryEntry[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }
}
